import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import more from "highcharts/highcharts-more";
import * as HighchartsGanttModule from 'highcharts/modules/gantt';
import {
    Box, Typography, Stack,
    Chip
} from "@mui/material";
import { useParams } from 'react-router-dom';
import { useLazyGetWbsLevelQuery } from "store/slices/apis/scheduleSlice";

import xrange from "highcharts/modules/xrange";
import moment from 'moment';
import SchedulePanel from "./SchedulePanel";



if (typeof more === "function") {
    more(Highcharts);
    xrange(Highcharts);
}

const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const getStatusColor = (point) => {
    if (Date.parse(point.custom?.startDate) > Date.now()) return "#38BDF8";
    if (
        point.custom?.projectCompletionPercentage <
        point.custom?.plannedPercentage
    )
        return "#E65F2B";
    return "#22C55E";
};

const LegendItem = ({ color, label, icon }) => (
    <Stack direction="row" spacing={1} alignItems="center">
        <Box
            sx={{
                width: '20px',
                height: '3px',
                borderRadius: 999,
                bgcolor: color,
            }}
        />
        <Typography
            variant="body1"
            sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: "#7D7F81",
                letterSpacing: '1%',
                fontFamily: "Outfit, sans-serif",
            }}
        >
            {icon &&
                <span style={{ color: "#d32f2f", fontSize: "13px" }}>⚠️</span>
            }
            {label}
        </Typography>
    </Stack>
);

const ganttModule = HighchartsGanttModule.default;
if (typeof ganttModule === "function") {
    ganttModule(Highcharts);
}

const getBarColor = ({
    startDate,
    plannedPercentage,
    projectCompletionPercentage
}) => {
    const now = Date.now();
    const start = Date.parse(startDate);


    // 1. Future task
    if (start > now) {
        return "#38BDF8"; // sky blue
    }
    //console.log("projectCompletionPercentage",projectCompletionPercentage);
    //console.log("plannedPercentage",plannedPercentage);

    // 2. Behind schedule
    if (parseFloat(projectCompletionPercentage) < parseFloat(plannedPercentage)) {
        return "#E65F2B"; // red
    }

    // 3. On track / completed
    return "#22C55E"; // green
};

const createNode = (data, isInitial = false) => {
    const end = Date.parse(data.endDate);
    const start = Date.parse(data.startDate);
    const planned = typeof data.plannedPercentage === "number"
        ? parseFloat((data.plannedPercentage / 100).toFixed(2))
        // convert 0–100 → 0–1
        : null;
    const progress =
        typeof data.projectCompletionPercentage === "number"
            ? parseFloat((data.projectCompletionPercentage / 100).toFixed(2))
            // convert 0–100 → 0–1
            : null;
    //const progress = data.projectCompletionPercentage?.toFixed(2);
    console.log("actual", progress.toFixed(1))
    console.log("planned", planned.toFixed(1))

    return {
        id: data.id,
        name: data.name,
        // parent: data.parentTaskId ?? undefined,
        // parent: data.parentTaskId ? VIRTUAL_ROOT_ID : undefined,
        parent: isInitial ? undefined : data.parentTaskId,
        start: start,
        end: end,
        progress: progress,
        planned: planned,
        hasChildren: data.isLeaf === false,
        children: [],
        childrenLoaded: false,
        loading: false,
        expanded: false,
        plannedPercentage: data.plannedPercentage,
        critical: data.critical,
        color: getBarColor({
            startDate: data.startDate,
            plannedPercentage: data.plannedPercentage,
            projectCompletionPercentage: data.projectCompletionPercentage
        })
    }
};

const findNode = (nodes, id) => {
    for (const n of nodes) {
        if (n.id === id) return n;
        const found = findNode(n.children, id);
        if (found) return found;
    }
    return null;
};

const updateNode = (nodes, id, updater) =>
    nodes.map(n => {
        if (n.id === id) return updater(n);
        return { ...n, children: updateNode(n.children, id, updater) };
    });

const flattenTree = (nodes) => {
    const result = [];
    const walk = (list) => {
        list.forEach(n => {
            result.push(n);
            if (n.children.length) walk(n.children);
        });
    };
    walk(nodes);
    return result;
};



const parseDate = (iso) => {
    const d = new Date(iso);
    // strip time + timezone
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const rootNode = {
    id: "1",
    text: "PROJECT",
    start_date: "2024-01-01",
    end_date: "2027-12-31",
    parent: 0,
    open: true
};
const mapApiTaskToGantt = (task) => {
    const start = parseDate(task.startDate);
    const end = parseDate(task.endDate);
    if (!start || !end) return null;

    return {
        id: String(task.id),
        text: task.name,
        start_date: start,
        end_date: end,
        parent: task.parentTaskId === "1" ? 0 : String(task.parentTaskId),

        open: false,
        $has_child: task.isLeaf === false,

        isLeaf: task.isLeaf,
        progress: (task.projectCompletionPercentage || 0) / 100
    };
};;

// const mapApiTaskToGantt = (task) => {
//     if (!task.startDate || !task.endDate) return null;

//     const start = parseDate(task.startDate);
//     const end = parseDate(task.endDate);

//     return {
//         id: String(task.id),
//         text: task.name,
//         start_date: start,
//         end_date: end,
//         parent: task.parentTaskId ? String(task.parentTaskId) : 0,
//         open: false,
//         $has_child: task.isLeaf === false,
//         isLeaf: task.isLeaf,
//         progress: (task.projectCompletionPercentage || 0) / 100
//     };
// };


// const ScheduleGantt = ({kpiData}) => {
//     const { projectId } = useParams();

//     console.log("kpiData",kpiData);
//     const  rootResponse  = kpiData?.data;//useGetWbsLevelQuery({ projectId });

//     const [treeData, setTreeData] = useState([]);

//     console.log("Root response", rootResponse);

//     useEffect(() => {
//         if (!rootResponse) return;
//         setTreeData(
//             rootResponse.map(d => createNode(d, true))
//         );


//         //setTreeData(rootResponse.data.map(createNode));
//     }, [rootResponse]);

//     const [
//         fetchWbsLevel,
//         { isFetching: isWbsLoading }
//     ] = useLazyGetWbsLevelQuery();


//     const handlePointClick = useCallback(async function () {
//         const point = this;
//         if (!point.hasChildren) return;

//         const node = findNode(treeData, point.id);
//         if (!node || node.childrenLoaded || node.loading) return;

//         setTreeData(prev =>
//             updateNode(prev, node.id, n => ({ ...n, loading: true }))
//         );

//         try {
//             const response = await fetchWbsLevel({
//                 projectId,
//                 parentId: node.id
//             }).unwrap();


//             const children = response?.data ?? [];


//             setTreeData(prev =>
//                 updateNode(prev, node.id, n => ({
//                     ...n,
//                     // children: children.map(createNode),
//                     children: children.map(c =>
//                         createNode({ ...c, parentTaskId: node.id })
//                     ),
//                     childrenLoaded: true,
//                     loading: false
//                 }))
//             );
//         } catch (e) {
//             console.error("Failed to load children", e);
//             setTreeData(prev =>
//                 updateNode(prev, node.id, n => ({ ...n, loading: false }))
//             );
//         }
//     }, [treeData]);


//     const flatData = useMemo(() => flattenTree(treeData), [treeData]);

//     const options = useMemo(() => ({
//         title: { text: "" },

//         series: [{
//             type: "gantt",
//             data: flatData.map(n => ({
//                 ...n,
//                 name: n.loading ? `${n.name} ⏳` : n.name,
//                 completed:
//                 {
//                     amount:n.progress,
//                 } ,

//             })),
//             point: {
//                 events: {
//                     click: handlePointClick
//                 }
//             },
//             dataLabels: [
//                 // 🔴 Critical icon – LEFT
//                 {
//                     enabled: true,
//                     align: 'left',
//                     verticalAlign: 'middle',
//                     x: 2,
//                     useHTML: true,
//                     formatter: function () {
//                     return this.point?.critical
//                         ? '<span style="color:#d32f2f;font-size:16px;">⚠️</span>'
//                         : '';
//                     }
//                 },

//                 // 📊 Progress text – CENTER
//                 {
//                     enabled: true,
//                     align: 'center',
//                     verticalAlign: 'middle',
//                     style: {
//                     fontSize: '12px',
//                     fontWeight: '600',
//                     color: '#ffffff',
//                     textOutline: 'none'
//                     },
//                     formatter: function () {
//                     const pct = Math.round((this.point.completed?.amount || 0) * 100);
//                     return pct ? `${pct}%` : '';
//                     }
//                 }
//             ]
//         }],

//         yAxis: {
//             uniqueNames: true
//         },
//         //xAxis: { type: "datetime" },
//         credits: {
//             enabled: false
//         },
//         tooltip: {
//             headerFormat: "",

//             pointFormatter: function () {
//                 const format = (ts) =>
//                     Highcharts.dateFormat("%e %b %Y", ts); // e.g. 17 Feb 2025

//                 return `
//                 <b>${this.name}</b><br/>
//                 Start: ${format(this.start)}<br/>
//                 End: ${format(this.end)}<br/>
//                 Progress: ${(this.progress*100).toFixed(2)}%<br/>
//                 Planned: ${(this.planned*100).toFixed(2)}%<br/>
//                 Critical: ${this.critical ? "Yes" : "No"}<br/>
//               `;
//             }
//         },

//     }), [flatData, handlePointClick]);

//     return (
//         <Card
//             elevation={0}
//             sx={{
//                 borderRadius: 3,
//                 border: "1px solid #E4E4E4",
//                 width: '100%',
//                 pb: 3,
//                 display: 'flex',
//                 flexDirection: 'column',
//                 overflow: 'hidden',
//             }}
//         >
//             <Box sx={{
//                 width: '100%',
//                 borderBottom: "1px solid #E4E4E4",
//                 p: 2,
//                 display: 'flex',
//                 justifyContent: 'space-between'
//             }}>
//                 <Box>
//                     <Typography sx={{
//                         fontFamily: "Outfit, sans-serif",
//                         fontWeight: 500,
//                         fontSize: '20px',
//                         letterSpacing: "0px",
//                         mb: 0,
//                         color: "#111216"
//                     }}>
//                         Project Schedule Timeline
//                     </Typography>
//                     <Typography sx={{
//                         fontFamily: "Outfit, sans-serif",
//                         fontWeight: 300,
//                         fontSize: '13px',
//                         letterSpacing: "0px",
//                         color: '#7D7F81'
//                     }}>
//                         Comprehensive View of Activity Timelines & Completion Status.
//                     </Typography>
//                 </Box>
//                 <Box>
//                     <Stack sx={{
//                         mr: '34px'
//                     }}>
//                         <LegendItem color="#22C55E" label="On time" />
//                         <LegendItem color="#E65F2B" label="Delayed" />
//                         <LegendItem color="#38BDF8" label="Future task" />
//                         <LegendItem icon={true} label="Critical" />
//                     </Stack>
//                 </Box>

//             </Box>

//             <HighchartsReact
//                 highcharts={Highcharts}
//                 constructorType="ganttChart"
//                 options={options}
//             />
//         </Card>

//     );
// };



const ScheduleGantt1 = ({ kpiData }) => {
    const { projectId } = useParams();

    const [currentParentId, setCurrentParentId] = useState(null);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [currentParentTask, setCurrentParentTask] = useState(null);
    console.log("currentParent", currentParentTask)



    const [childrenMap, setChildrenMap] = useState({});

    const [fetchWbsLevel] = useLazyGetWbsLevelQuery();


    useEffect(() => {
        if (!kpiData?.data) return;
        console.log(kpiData?.data);

        setChildrenMap({
            null: kpiData.data,
        });
    }, [kpiData]);


    const loadChildren = useCallback(
        async (parentId, force = false) => {
            console.log(childrenMap);

            if (!force && childrenMap[parentId]) return;

            // if (childrenMap[parentId]) return;

            const res = await fetchWbsLevel({
                projectId,
                parentId,
            }).unwrap();

            setChildrenMap((prev) => ({
                ...prev,
                [parentId]: res?.data || [],
            }));
        },
        [childrenMap, fetchWbsLevel, projectId]
    );


    // const visibleTasks = useMemo(() => {
    //     return childrenMap[currentParentId] || [];
    // }, [childrenMap, currentParentId]);

    const visibleTasks = useMemo(() => {
        const children = childrenMap[currentParentId] || [];
        console.log("visibleTasks", children, currentParentId, childrenMap)
        const statusDate = moment("2026-02-27T00:00:00");
        // Root level (initial load)
        if (!currentParentId) {
            // return children.map((t) => ({
            //     ...t,
            //     isParent: false,
            // }));
            return children.map(item => {
                const startDate = moment(item?.startDate);
                const endDate = moment(item?.endDate);
                //const statusDate = moment("2026-01-30T00:00:00");
                const daysLapsed = statusDate.diff(startDate, "days");
                const totalDuration = endDate.diff(startDate, "days")
                const durationPercentage = daysLapsed / totalDuration * 100;
                const updatedDurationPercentage = item.percentageComplete === 0 ? 0 : durationPercentage
                const zeroDays = endDate - startDate;
                const newStartDate = zeroDays === 0 ? startDate.subtract(1, "day").format("YYYY-MM-DD") : startDate;
                console.log("zeroDays", zeroDays, newStartDate, endDate)
                console.log("daysLapsed1", daysLapsed, totalDuration, durationPercentage, startDate, endDate, newStartDate);
                return { ...item, fillPercentage: updatedDurationPercentage, isParent: false, updatedStartDate: newStartDate }
            })
            // return children;
        }
        console.log("currentParentTask", currentParentTask)
        //const daysLapsed = new Date(currentParentTask?.endDate) - new Date("2026-01-30T00:00:00")
        const startDate = moment(currentParentTask?.startDate);
        const endDate = moment(currentParentTask?.endDate);
        //const statusDate = moment("2026-01-30T00:00:00");
        const daysLapsed = statusDate.diff(startDate, "days");
        const totalDuration = endDate.diff(startDate, "days")
        const durationPercentage = daysLapsed / totalDuration * 100;
        const updatedDurationPercentage = currentParentTask.percentageComplete === 0 ? 0 : durationPercentage
        const zeroDays = endDate - startDate;
        const newStartDate = zeroDays === 0 ? startDate.subtract(1, "day").format("YYYY-MM-DD") : startDate;
        console.log("daysLapsed zero", daysLapsed, totalDuration, typeof children, newStartDate);

        const updatedChildrens = children.map(item => {
            const startDate = moment(item?.startDate);
            const endDate = moment(item?.endDate);
            //const statusDate = moment("2026-01-30T00:00:00");
            const daysLapsed = statusDate.diff(startDate, "days");
            const totalDuration = endDate.diff(startDate, "days")
            const durationPercentage = daysLapsed / totalDuration * 100;
            const updatedDurationPercentage = item.percentageComplete === 0 ? 0 : durationPercentage
            const zeroDays = endDate - startDate;
            const newStartDate = zeroDays === 0 ? startDate.subtract(1, "day").format("YYYY-MM-DD") : startDate;
            console.log("daysLapsed1", daysLapsed, totalDuration, durationPercentage, startDate, endDate);
            return { ...item, fillPercentage: updatedDurationPercentage, updatedStartDate: newStartDate }
        })

        console.log("updatedChildren", updatedChildrens, children);


        // Two-level view: parent + its children
        return [
            {
                id: currentParentId,
                name: currentParentTask?.name,
                startDate: currentParentTask?.startDate,
                endDate: currentParentTask?.endDate,
                isParent: true,
                projectCompletionPercentage: currentParentTask?.projectCompletionPercentage,
                plannedPercentage: currentParentTask?.plannedPercentage,
                percentageComplete: currentParentTask?.percentageComplete,
                fillPercentage: updatedDurationPercentage,
                updatedStartDate: newStartDate
            },
            ...updatedChildrens,
        ];
    }, [childrenMap, currentParentId, currentParentTask]);

    console.log("visibleTasks", visibleTasks)


    const onBarClick = useCallback(function () {
        const task = this;

        // console.log(task);

        if (!task.custom?.hasChildren) return;

        // setBreadcrumbs((b) => [...b, currentParentId]);
        setBreadcrumbs((prev) => [
            ...prev,
            {
                id: task.id,
                name: task.name,
                startDate: task?.custom?.startDate,
                endDate: task?.custom?.endDate,
                projectCompletionPercentage: task?.custom?.projectCompletionPercentage,
                plannedPercentage: task?.custom?.plannedPercentage,
                percentageComplete: task?.custom?.percentageComplete,
                parentId: task?.custom?.parentId
            },
        ]);

        setCurrentParentId(task.id);

        setCurrentParentTask({
            id: task.id,
            name: task.name,
            startDate: task?.custom?.startDate,
            endDate: task?.custom?.endDate,
            projectCompletionPercentage: task?.custom?.projectCompletionPercentage,
            plannedPercentage: task?.custom?.plannedPercentage,
            percentageComplete: task?.custom?.percentageComplete
        });

        loadChildren(task.id);

    }, [currentParentId, loadChildren]);

    console.log(breadcrumbs);

    // const onBarClick = useCallback(function () {
    //     const task = this;

    //     if (!task.custom?.hasChildren) return;

    //     setBreadcrumbs((b) => [...b, currentParentId]);
    //     setCurrentParentId(task.id);

    //     loadChildren(task.id);
    // }, [currentParentId, loadChildren]);


    // console.log(visibleTasks);

    // console.log(visibleTasks.map((t) => ({
    //     id: t.id,
    //     name: t.name,
    //     start: Date.parse(t.startDate),
    //     end: Date.parse(t.endDate),
    //     color:
    //         Date.parse(t.startDate) > Date.now()
    //             ? "#38BDF8"
    //             : t.projectCompletionPercentage < t.plannedPercentage
    //                 ? "#E65F2B"
    //                 : "#22C55E",
    //     custom: {
    //         hasChildren: t.isLeaf === false,
    //     },
    // })));


    const options = useMemo(
        () => ({
            chart: { animation: false },
            title: { text: "" },
            credits: { enabled: false },
            tooltip: {
                useHTML: true,
                backgroundColor: "transparent",
                borderWidth: 0,
                shadow: false,
                outside: true,

                formatter: function () {
                    const { point } = this;
                    const c = point.custom || {};

                    return `
            <div style="
                padding:12px 14px;
                min-width:240px;
                border-radius:8px;
                background:#ffffff;
                box-shadow:0 4px 12px rgba(0,0,0,0.15);
                font-size:13px;
            ">
                <div style="
                    font-weight:600;
                    font-size:14px;
                    margin-bottom:6px;
                    color:#182A4E;
                ">
                    ${point.name}
                </div>

                <div style="margin-bottom:6px">
                    <strong>Start:</strong> ${formatDate(c.startDate)}<br/>
                    <strong>End:</strong> ${formatDate(c.endDate)}
                </div>

                <div style="margin-bottom:6px">
                    <strong>Progress:</strong> ${c.percentageComplete?.toFixed(2) ?? "-"}%<br/>
                    <strong>Planned:</strong> ${c.plannedPercentage?.toFixed(2) ?? "-"}%                    
                </div>

                <div style="
                    display:flex;
                    align-items:center;
                    gap:6px;
                ">
                   
                    <strong>Critical:</strong> ${c.critical ? "Yes" : "No"}<br/>
                </div>
            </div>
        `;
                },
            },

            // tooltip: { enabled: true },
            yAxis: { uniqueNames: true },
            series: [
                {
                    type: "gantt",
                    data: visibleTasks.map((t) => ({
                        id: t.id,
                        name: t.name,
                        start: Date.parse(t.updatedStartDate),
                        end: Date.parse(t.endDate),
                        // parent: t?.parentTaskId,
                        ...(visibleTasks[0]?.isParent && {
                            parent: t?.parentTaskId,
                        }),
                        completed: {
                            //amount: t?.percentageComplete / 100
                            amount: t.fillPercentage / 100
                        },
                        color: t?.critical ? "#E65F2B" : Date.parse(t.startDate) > Date.now()
                            ? "grey"
                            : t.projectCompletionPercentage < t.plannedPercentage
                                ? "red"
                                : "#22C55E",
                        // color:
                        //     Date.parse(t.startDate) > Date.now()
                        //         ? "#38BDF8"
                        //         : t.projectCompletionPercentage < t.plannedPercentage
                        //             ? "#E65F2B"
                        //             : "#22C55E",
                        custom: {
                            hasChildren: t.isLeaf === false,
                            startDate: t.startDate,
                            endDate: t.endDate,
                            projectCompletionPercentage: t?.projectCompletionPercentage,
                            plannedPercentage: t?.plannedPercentage,
                            percentageComplete: t?.percentageComplete,
                            critical: Date.parse(t.startDate) == Date.parse(t.endDate) ? false : t?.critical,
                            parentId: t?.parentTaskId
                        },
                    })),
                    point: {
                        events: {
                            click: onBarClick,
                        },
                    },
                    dataLabels: [
                        // 🔴 Critical icon – LEFT
                        {
                            enabled: true,
                            align: 'left',
                            verticalAlign: 'middle',
                            x: 2,
                            useHTML: true,
                            formatter: function () {
                                return this.point?.custom?.critical
                                    ? '<span style="color:#d32f2f;font-size:16px;">⚠️</span>'
                                    : '';
                            }
                        },
                        {
                            enabled: true,
                            align: 'center',
                            verticalAlign: 'middle',
                            style: {
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#ffffff',
                                textOutline: 'none'
                            },
                            formatter: function () {
                                const pct = Math.round((this.point?.custom?.percentageComplete || 0));
                                return pct ? `${pct}%` : '';
                            }
                        }
                    ]
                    // dataLabels: {
                    //     enabled: true,
                    //     align: 'left',
                    //     verticalAlign: 'middle',
                    //     x: 2,
                    //     useHTML: true,
                    //     formatter: function () {
                    //         return this.point?.custom?.critical
                    //             ? '<span style="color:#d32f2f;font-size:16px;">⚠️</span>'
                    //             : '';
                    //     }
                    // },
                },
            ],
        }),
        [visibleTasks, onBarClick]
    );


    return (
        <SchedulePanel
            eyebrow="Delivery timeline"
            title="Integrated Schedule"
            subtitle="Review activity sequencing, completion status, delay exposure, and critical-path context."
            legend={
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "flex-start", sm: "center" }}>
                    <LegendItem color="#22C55E" label="On time" />
                    <LegendItem color="#B91C1C" label="Delayed" />
                    <LegendItem color="#64748B" label="Future task" />
                    <LegendItem color="#E65F2B" icon={true} label="Critical" />
                </Stack>
            }
            bodySx={{ pb: 1 }}
        >
            <Box sx={{ width: '100%', px: 1.5, py: 1, borderBottom: "1px solid #E7ECF3", backgroundColor: "#F8FAFC" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    {breadcrumbs.map((crumb, index) => (
                        <React.Fragment key={crumb.id}>
                            <Chip
                                label={`${crumb?.name}`}
                                variant="outlined"
                                color="text.secondary"
                                sx={{ border: 0 }}
                                onDelete={async () => {
                                    console.log(crumb);
                                    const parentId = index === 919 ? null : crumb.parentId;

                                    // 1. Jump to selected breadcrumb
                                    setCurrentParentId(parentId);

                                    // 2. Trim breadcrumbs
                                    setBreadcrumbs((b) => b.slice(0, index));

                                    setCurrentParentTask({
                                        id: crumb.id,
                                        name: crumb.name,
                                        startDate: crumb?.startDate,
                                        endDate: crumb?.endDate,
                                        projectCompletionPercentage: crumb?.projectCompletionPercentage,
                                        plannedPercentage: crumb?.plannedPercentage,
                                        percentageComplete: crumb?.percentageComplete
                                    });

                                    // 3. Load children explicitly
                                    await loadChildren(parentId, true);
                                }}

                            />

                            {index < breadcrumbs.length - 1 && (
                                <Typography color="text.secondary">/</Typography>
                            )}
                        </React.Fragment>
                    ))}
                </Stack>
            </Box>

            <HighchartsReact
                highcharts={Highcharts}
                constructorType="ganttChart"
                options={options}
            />
        </SchedulePanel>
    );
};

// const ScheduleGantt2 = ({ kpiData }) => {
//     const { projectId } = useParams();

//     const [currentParentId, setCurrentParentId] = useState(null);
//     const [breadcrumbs, setBreadcrumbs] = useState([]);
//     const [childrenMap, setChildrenMap] = useState({});

//     const [fetchWbsLevel] = useLazyGetWbsLevelQuery();


//     useEffect(() => {
//         if (!kpiData?.data) return;
//         setChildrenMap({ null: kpiData.data });
//     }, [kpiData]);

//     const loadChildren = async (parentId) => {
//         if (childrenMap[parentId]) return;

//         const res = await fetchWbsLevel({ projectId, parentId }).unwrap();
//         setChildrenMap(prev => ({
//             ...prev,
//             [parentId]: res?.data || []
//         }));
//     };

//     const visibleTasks = useMemo(() => {
//         return childrenMap[currentParentId] || [];
//     }, [childrenMap, currentParentId]);

//     const chartData = useMemo(() => {
//         return visibleTasks.map((t, index) => ({
//             id: String(t.id),
//             x: Date.parse(t.startDate),
//             x2: Date.parse(t.endDate),
//             y: index,
//             color:
//                 Date.parse(t.startDate) > Date.now()
//                     ? "#38BDF8"
//                     : t.projectCompletionPercentage < t.plannedPercentage
//                         ? "#E65F2B"
//                         : "#22C55E",
//             custom: {
//                 id: t.id,
//                 name: t.name,
//                 isLeaf: t.isLeaf
//             }
//         }));
//     }, [visibleTasks]);

//     const options = {
//         chart: {
//             type: "xrange",
//             animation: false
//         },
//         title: { text: "" },
//         credits: { enabled: false },
//         tooltip: { enabled: false },
//         xAxis: { type: "datetime" },
//         yAxis: {
//             categories: visibleTasks.map(t =>
//                 t.isLeaf ? `  ${t.name}` : `▶ ${t.name}`
//             ),
//             reversed: true
//         },
//         series: [{
//             data: chartData,
//             point: {
//                 events: {
//                     click() {
//                         const task = this.custom;
//                         if (!task || task.isLeaf) return;

//                         setBreadcrumbs(b => [...b, currentParentId]);
//                         setCurrentParentId(task.id);
//                         loadChildren(task.id);
//                     }
//                 }
//             }
//         }]
//     };

//     return (
//         <Card>
//             <Box p={2} display="flex" justifyContent="space-between">
//                 <Typography fontSize={20}>Project Schedule Timeline</Typography>

//                 <Button
//                     disabled={!breadcrumbs.length}
//                     onClick={() => {
//                         const prev = breadcrumbs[breadcrumbs.length - 1];
//                         setBreadcrumbs(b => b.slice(0, -1));
//                         setCurrentParentId(prev);
//                     }}
//                 >
//                     Back
//                 </Button>
//             </Box>

//             <HighchartsReact
//                 highcharts={Highcharts}
//                 options={options}
//             />
//         </Card>
//     );
// };


// const ScheduleGanttDhtmlx = ({ rootData }) => {
//     const ganttRef = useRef(null);
//     const { projectId } = useParams();
//     const [fetchWbsLevel] = useLazyGetWbsLevelQuery();

//     useEffect(() => {
//         gantt.clearAll();

//         gantt.config.readonly = true;
//         gantt.config.open_tree_initially = false;

//         gantt.config.scales = [
//             { unit: "year", step: 1, format: "%Y" },
//             { unit: "month", step: 1, format: "%M" }
//         ];

//         gantt.config.scale_height = 60;
//         gantt.config.row_height = 48;
//         gantt.config.bar_height = 18;

//         gantt.config.show_task_cells = false;
//         gantt.config.show_progress = false;

//         gantt.config.columns = [
//             { name: "text", label: "Task name", tree: true, width: "*" },
//             { name: "start_date", label: "Start time", align: "center", width: 90 },
//             { name: "duration", label: "Duration", align: "center", width: 70 }
//         ];



//         gantt.init(ganttRef.current);


//         const rootTasks = rootData
//             .map(mapApiTaskToGantt)
//             .filter(Boolean);

//         //gantt.parse({ data: rootTasks });
//         gantt.parse({
//             data: [...rootTasks]
//         });

//         gantt.attachEvent("onTaskClick", async (id) => {
//             console.log("event fired", id);
//             const task = gantt.getTask(id);
//             if (task.childrenLoaded || task.isLeaf) return true;

//             try {
//                 const res = await fetchWbsLevel({ projectId, parentId: id }).unwrap();
//                 const children = (res?.data || [])
//                     .map(mapApiTaskToGantt)
//                     .filter(Boolean);

//                 children.forEach(child => gantt.addTask(child, id));

//                 task.childrenLoaded = true;
//                 gantt.updateTask(id);
//             } catch (e) {
//                 console.error("Failed to load children", e);
//             }

//             return true;
//         });

//         return () => gantt.clearAll();
//     }, [rootData, projectId, fetchWbsLevel]);


//     return (
//         <div
//             ref={ganttRef}
//             style={{
//                 width: "100%",
//                 height: "600px",

//             }}
//         />
//     );
// };




const ScheduleGantt = ({ kpiData }) => {
    // const { projectId } = useParams();
    // const { data: kpiData, isLoading } = useGetWbsLevelQuery({
    //     projectId,
    //     parentId: null
    // });

    // if (isLoading) return <div>Loading...</div>;

    return (
        <>
            <ScheduleGantt1 kpiData={kpiData} />
            {/* <ScheduleGantt2 kpiData={kpiData} />
            <ScheduleGanttDhtmlx rootData={kpiData?.data || []} /> */}
        </>
    );
}


export default ScheduleGantt;
